import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PostStoreService {
  private postsSubject = new BehaviorSubject<any[]>([]);
  posts$ = this.postsSubject.asObservable();

  getPosts(): any[] {
    return this.postsSubject.getValue();
  }

  setPosts(posts: any[]) {
    const current = this.getPosts();

    const initialized = posts.map(post => {
      const existing = current.find(p => p.id === post.id);
      return {
        ...post,
        // preserve UI state if already present
        showReplies: existing?.showReplies ?? false,
        replies: existing?.replies ?? [],
        repliesLoaded: existing?.repliesLoaded ?? false,
      };
    });

    this.postsSubject.next(initialized);
  }

  addPost(newPost: any) {
    newPost.showReplies = false;
    newPost.replies = [];
    const updated = [newPost, ...this.getPosts()];
    this.setPosts(updated);
  }

  updatePost(postId: number, updatedPost: any) {
    const updated = this.getPosts().map(post => {
      if (post.id === postId) {
        return {
          ...updatedPost,
          replies: post.replies ?? [],
          repliesLoaded: post.repliesLoaded ?? false,
          showReplies: post.showReplies ?? false
        };
      }
      return post;
    });

    this.setPosts(updated);
  }

  appendPosts(newPosts: any[]) {
    const initialized = newPosts.map(post => ({
      ...post,
      showReplies: false,
      replies: [],
      repliesLoaded: false,
    }));
    const combined = [...this.getPosts(), ...initialized];
    this.setPosts(combined);
  }

  replaceRepliesToPost(parentPostId: number, replies: any[]) {
    const posts = [...this.getPosts()];

    const insertReplies = (items: any[]): boolean => {
      for (const post of items) {
        if (post.id === parentPostId) {
          post.replies = replies;
          post.showReplies = true;
          post.repliesLoaded = true;
          return true;
        }
        if (post.replies?.length) {
          const found = insertReplies(post.replies);
          if (found) return true;
        }
      }
      return false;
    };

    if (insertReplies(posts)) {
      this.setPosts(posts);
    }
  }

  /**
   * Inserts a reply directly under its parent post in the nested structure.
   * Also bumps the parent's reply count.
   * Returns true if inserted, false if parent not found.
   */
  insertReplyDirectly(parentPostId: number, newReply: any): boolean {
    const updateReplies = (posts: any[]): boolean => {
      for (const post of posts) {
        if (post.id === parentPostId) {
          post.replies = [...(post.replies || []), newReply];
          post.repliesLoaded = true;

          if (!post._count) post._count = { replies: 1 };
          else post._count.replies = (post._count.replies || 0) + 1;

          return true;
        }

        if (post.replies?.length) {
          const found = updateReplies(post.replies);
          if (found) return true;
        }
      }
      return false;
    };

    const posts = this.getPosts();
    const inserted = updateReplies(posts);
    if (inserted) {
      this.setPosts(posts);
    }
    return inserted;
  }

  /**
   * Safely increments/decrements reply count for a parent post (top-level).
   * No-ops if the post isn't present.
   */
  bumpRepliesCount(parentPostId: number, delta: number = 1): void {
    const updated = this.getPosts().map(p => {
      if (p.id === parentPostId) {
        const nextCount = Math.max(0, (p._count?.replies || 0) + delta);
        return { ...p, _count: { ...(p._count || {}), replies: nextCount } };
      }
      return p;
    });
    this.setPosts(updated);
  }

  /**
   * Marks the parent post as applied by the current user (only if it's a job).
   * No-ops if the post isn't found or isn't a job.
   */
markAppliedIfJob(parentPostId: number): void {
  const updated = this.getPosts().map(p => {
    if (
      p.id === parentPostId &&
      p.post_type === 'job' &&
      !p.is_posted_by_ka_recruiter
    ) {
      return { ...p, has_applied_by_me: true };
    }
    return p;
  });
  this.setPosts(updated);
}

  removePost(postId: number) {
    const updated = this.getPosts().filter(p => p.id !== postId);
    this.setPosts(updated);
  }

  clear() {
    this.postsSubject.next([]);
  }
}
